/* eslint-disable @typescript-eslint/no-explicit-any */

import { Box, Button, Stack, Text, Badge } from '@chakra-ui/react';
import { useState } from 'react';

import { fetchAPI } from '@/api/fetch';
import {
  apiAdminCreateMilitaryTranslationSample,
  AdminCreateMilitaryTranslationSamplePayload
} from '@/api/services';
import FileUploadCard from '@/components/forms/FileUploadCard';
import { MilitaryTranslationSample } from '@/types/military_translation_sample';
import { MAX_RESUME_FILE_SIZE, MAX_RESUME_FILE_SIZE_MB } from '@/config/constants';

const FILE_SIZE_LIMIT = MAX_RESUME_FILE_SIZE;
const MAX_FILE_SIZE_MB = MAX_RESUME_FILE_SIZE_MB;

export default function MilitaryTranslationSampleSection() {
  const [militaryFile, setMilitaryFile] = useState<File | null>(null);
  const [corporateFile, setCorporateFile] = useState<File | null>(null);
  const [militaryError, setMilitaryError] = useState<string | null>(null);
  const [corporateError, setCorporateError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<MilitaryTranslationSample | null>(null);

  const validateFile = (file: File): string | null => {
    if (file.size > FILE_SIZE_LIMIT) return `File size exceeds ${MAX_FILE_SIZE_MB} MB`;
    return null;
  };

  const handleMilitaryFileChange = (file: File) => {
    setMilitaryError(validateFile(file));
    setMilitaryFile(file);
    setResult(null);
  };

  const handleCorporateFileChange = (file: File) => {
    setCorporateError(validateFile(file));
    setCorporateFile(file);
    setResult(null);
  };

  const handleSubmit = async () => {
    if (!militaryFile || !corporateFile) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);

    try {
      const payload: AdminCreateMilitaryTranslationSamplePayload = {
        military_file: militaryFile,
        corporate_file: corporateFile
      };

      const response = await fetchAPI<MilitaryTranslationSample>({
        request: () => apiAdminCreateMilitaryTranslationSample({ payload })
      });

      if (response.success) {
        setResult(response.data);
      } else {
        setSubmitError(response.message ?? 'Failed to create military translation sample');
      }
    } catch (error: any) {
      console.error('Unable to create military translation sample', error);
      setSubmitError('Unable to create military translation sample');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    militaryFile && corporateFile && !militaryError && !corporateError && !submitting;

  return (
    <Stack gap="24px" width="100%">
      <Stack gap="16px">
        <Text fontSize="16px" fontWeight="semibold" color="neutrals.700">
          Military File
        </Text>
        <FileUploadCard
          file={militaryFile}
          uploading={false}
          error={militaryError}
          maxSizeMB={MAX_FILE_SIZE_MB}
          onChange={handleMilitaryFileChange}
          onReset={() => {
            setMilitaryFile(null);
            setMilitaryError(null);
            setResult(null);
          }}
        />
      </Stack>

      <Stack gap="16px">
        <Text fontSize="16px" fontWeight="semibold" color="neutrals.700">
          Corporate File
        </Text>
        <FileUploadCard
          file={corporateFile}
          uploading={false}
          error={corporateError}
          maxSizeMB={MAX_FILE_SIZE_MB}
          onChange={handleCorporateFileChange}
          onReset={() => {
            setCorporateFile(null);
            setCorporateError(null);
            setResult(null);
          }}
        />
      </Stack>

      <Box>
        <Button
          colorPalette="blue"
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={submitting}
          loadingText="Creating..."
        >
          Create Sample
        </Button>
        {submitError && (
          <Text fontSize="14px" color="error.700" mt="8px">
            {submitError}
          </Text>
        )}
      </Box>

      <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
        Result
      </Text>

      {result ? (
        <Stack gap="16px">
          <Stack gap="8px">
            <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
              Sample ID
            </Text>
            <Text fontSize="14px" color="neutrals.800" fontFamily="mono">
              {result.sample_id}
            </Text>
          </Stack>

          {result.military_types.length > 0 && (
            <Stack gap="8px">
              <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
                Military Types
              </Text>
              <Box display="flex" flexWrap="wrap" gap="8px">
                {result.military_types.map((type, index) => (
                  <Badge
                    key={index}
                    colorPalette="blue"
                    variant="subtle"
                    fontSize="13px"
                    px="10px"
                    py="4px"
                    borderRadius="6px"
                  >
                    {type}
                  </Badge>
                ))}
              </Box>
            </Stack>
          )}

          <Stack gap="8px">
            <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
              Military Text
            </Text>
            <Box
              p="16px"
              borderRadius="8px"
              border="1px solid"
              borderColor="neutrals.200"
              bg="neutrals.50"
              fontSize="14px"
              color="neutrals.800"
              whiteSpace="pre-wrap"
            >
              {result.military_text}
            </Box>
          </Stack>

          <Stack gap="8px">
            <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
              Corporate Text
            </Text>
            <Box
              p="16px"
              borderRadius="8px"
              border="1px solid"
              borderColor="neutrals.200"
              bg="neutrals.50"
              fontSize="14px"
              color="neutrals.800"
              whiteSpace="pre-wrap"
            >
              {result.corporate_text}
            </Box>
          </Stack>

          <Stack gap="8px">
            <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
              Embedding Vector
            </Text>
            <Box
              bg="neutrals.50"
              border="1px solid"
              borderColor="neutrals.200"
              borderRadius="8px"
              p="12px"
              maxH="200px"
              overflowY="auto"
            >
              <Text fontSize="12px" color="neutrals.700" fontFamily="mono">
                [{result.embedding.slice(0, 20).join(', ')}
                {result.embedding.length > 20 ? ', ...' : ''}]
              </Text>
              <Text fontSize="12px" color="neutrals.500" mt="4px">
                {result.embedding.length} dimensions
              </Text>
            </Box>
          </Stack>
        </Stack>
      ) : (
        <Text>No result yet.</Text>
      )}
    </Stack>
  );
}
